import React from 'react';
import {AlertCircle} from 'lucide-react';

/**
 * Preparatory stage self-assessment component with emoji-based UI (READ-ONLY)
 * Designed to match the UI in the provided image
 */
const PreparatorySelfAssessment = ({submission}) => {
    // If no self-assessment data is available, show placeholder
    if (!submission || !submission.self_assessment) {
        return (
            <div className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-orange-200">
                <div className="px-4 py-3 bg-orange-500 text-white">
                    <h2 className="text-lg font-medium">Self-Assessment</h2>
                    <p className="mt-1 max-w-2xl text-sm">
                        Student's reflection on the completed activity
                    </p>
                </div>
                <div className="p-6 text-center">
                    <AlertCircle className="h-12 w-12 text-orange-200 mx-auto mb-3"/>
                    <p className="text-gray-500">No self-assessment has been submitted yet.</p>
                </div>
            </div>
        );
    }

    // Display the submitted assessment in a format matching the reference image
    const assessment = submission.self_assessment;

    return (
        <div className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-orange-200">
            <div className="px-4 py-3 bg-orange-500 text-white">
                <h2 className="text-lg font-medium">Self-Assessment</h2>
                <p className="mt-1 max-w-2xl text-sm">
                    Please answer the following regarding the activity you just completed.
                </p>
            </div>

            <div className="p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Colour the emoji for each statement.</p>

                <table className="min-w-full border border-orange-200">
                    <tbody>
                    {/* Row 1 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-6 py-4 text-sm text-gray-900 border-r border-orange-200 w-1/2">
                            I followed my teacher's instructions.
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex justify-around">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.followed_instructions === "Yes" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😊</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">yes</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.followed_instructions === "No" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">no</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.followed_instructions === "Not sure" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">🤔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">not sure</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Row 2 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-6 py-4 text-sm text-gray-900 border-r border-orange-200">
                            I liked doing this work.
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex justify-around">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.liked_work === "Yes" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😊</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">yes</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.liked_work === "No" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">no</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.liked_work === "Not sure" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">🤔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">not sure</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Row 3 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-6 py-4 text-sm text-gray-900 border-r border-orange-200">
                            I asked for help if I didn't understand.
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex justify-around">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.asked_for_help === "Yes" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😊</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">yes</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.asked_for_help === "No" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">no</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.asked_for_help === "Not sure" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">🤔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">not sure</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Row 4 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-6 py-4 text-sm text-gray-900 border-r border-orange-200">
                            I tried my best in this task.
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex justify-around">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.tried_my_best === "Yes" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😊</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">yes</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.tried_my_best === "No" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">no</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.tried_my_best === "Not sure" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">🤔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">not sure</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Row 5 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-6 py-4 text-sm text-gray-900 border-r border-orange-200">
                            I am proud of my work.
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex justify-around">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.proud_of_work === "Yes" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😊</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">yes</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.proud_of_work === "No" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">no</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.proud_of_work === "Not sure" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">🤔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">not sure</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Row 6 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-6 py-4 text-sm text-gray-900 border-r border-orange-200">
                            I want to do this task again.
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex justify-around">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.want_to_repeat === "Yes" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😊</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">yes</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.want_to_repeat === "No" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">no</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.want_to_repeat === "Not sure" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">🤔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">not sure</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Row 7 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-6 py-4 text-sm text-gray-900 border-r border-orange-200">
                            I liked working with my classmate/s.
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex justify-around">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.liked_groupwork === "Yes" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😊</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">yes</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.liked_groupwork === "No" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">no</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.liked_groupwork === "Not sure" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">🤔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">not sure</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Row 8 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-6 py-4 text-sm text-gray-900 border-r border-orange-200">
                            I could ask my classmates for help, and they helped me.
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex justify-around">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.asked_classmate_help === "Yes" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😊</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">yes</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.asked_classmate_help === "No" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">😔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">no</span>
                                </div>

                                <div className="h-10 self-center border-r border-dashed border-gray-300"></div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${assessment.asked_classmate_help === "Not sure" ? 'border-black bg-white' : 'border-gray-300'}`}>
                                        <span className="text-xl">🤔</span>
                                    </div>
                                    <span className="mt-1 text-xs font-medium">not sure</span>
                                </div>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PreparatorySelfAssessment;